import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './MultiSelectFilter.css';

// Checkbox-dropdown multi-select for a filter bar field (e.g. HC Category,
// Status, Country) — lets several values be selected at once instead of the
// single value a plain <select> allows. Same interaction/portal pattern as
// LegalMain.js's own MultiSelectFilter (click the button, check boxes, click
// outside to close; panel renders through a portal into document.body so a
// horizontal-scrolling filter row never clips it), generalized here under
// `dash-ms-*` class names so every department dashboard's filter bar can
// share one implementation instead of each duplicating it.
// An option is either a plain string (value === label, the common case) or
// an { value, label } pair — needed where the displayed text is dynamic
// (e.g. Fiscal Year's "FY 2026") but the stored/compared value must stay a
// stable token ("current") regardless of what label currently shows.
const optionValue = (o) => (o && typeof o === 'object' ? o.value : o);
const optionLabel = (o) => (o && typeof o === 'object' ? o.label : o);

export function MultiSelectFilter({ label, options, selected, onChange, disabled, title }) {
    const [open, setOpen] = useState(false);
    const [rect, setRect] = useState(null);
    const wrapRef = useRef(null);
    const panelRef = useRef(null);

    const updateRect = useCallback(() => {
        if (wrapRef.current) setRect(wrapRef.current.getBoundingClientRect());
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        updateRect();
        const close = (e) => {
            if (wrapRef.current && wrapRef.current.contains(e.target)) return;
            if (panelRef.current && panelRef.current.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', close);
        window.addEventListener('scroll', updateRect, true);
        window.addEventListener('resize', updateRect);
        return () => {
            document.removeEventListener('mousedown', close);
            window.removeEventListener('scroll', updateRect, true);
            window.removeEventListener('resize', updateRect);
        };
    }, [open, updateRect]);

    const toggle = (value) => {
        onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
    };
    const summary = selected.length ? `${selected.length} selected` : 'All';
    return (
        <label className={`dash-filter-group${selected.length ? ' active' : ''}`} title={title}>
            <span className="dash-filter-label">{label}</span>
            <div className="dash-ms" ref={wrapRef}>
                <button type="button" className="dash-ms-btn dash-filter-select" disabled={disabled} onClick={() => setOpen((o) => !o)}>
                    {summary}
                </button>
                {open && rect && createPortal(
                    <div
                        className="dash-ms-panel dash-ms-panel--floating"
                        role="listbox" aria-multiselectable="true"
                        ref={panelRef}
                        style={{ position: 'fixed', top: rect.bottom + 4, left: rect.left, minWidth: rect.width }}
                    >
                        {options.map((o) => {
                            const value = optionValue(o);
                            return (
                                <label key={value} className="dash-ms-option">
                                    <input type="checkbox" checked={selected.includes(value)} onChange={() => toggle(value)} />
                                    {optionLabel(o)}
                                </label>
                            );
                        })}
                        {!options.length && <div className="dash-ms-empty">No options</div>}
                    </div>,
                    document.body,
                )}
            </div>
        </label>
    );
}
